// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Icon,
  //   Divider,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import { PinInput } from "react-input-pin-code";
import usePageTitle from "app/pageTitle";

function OrderGiftCard() {
  const { reference } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [giftCard, setGiftCard] = useState(null);
  usePageTitle(giftCard === null ? "Buy Gift Card" : `${giftCard.title} Gift Card`);

  const [orderValue, setOrderValue] = useState(null);
  const [orderQuantity, setOrderQuantity] = useState(1);

  const [calculated, setCalculated] = useState(false);
  const [debitAmount, setDebitAmount] = useState(null);

  const [userPin, setUserPin] = useState(null);
  const [pinValues, setPinValues] = useState(["", "", "", ""]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [purchased, setPurchase] = useState(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`giftcards/${reference}/get.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setGiftCard(result.data.data.gift_card);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Please try again in a few moments.");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  useEffect(() => {
    setCalculated(false);
    if (giftCard === null) {
      return;
    }

    if (orderQuantity === null || orderValue === null) {
      return;
    }

    const ex =
      orderQuantity * giftCard.fee + (orderQuantity * orderValue * giftCard.exchange_rate) / 100;
    setDebitAmount(ex / 100);

    setCalculated(true);
  }, [orderQuantity, orderValue]);

  function increment() {
    setOrderQuantity(parseInt(orderQuantity, 10) + 1);
  }

  function decrement() {
    if (orderQuantity > 1) {
      setOrderQuantity(parseInt(orderQuantity, 10) - 1);
    }
  }

  async function purchase() {
    if (!calculated) {
      return;
    }

    if (userPin === null) {
      setSeverity("error");
      setErrMsg("Enter your pin to authorize this transaction.");
      setOpen(true);
      return;
    }

    const searchParams = {
      reference: `${reference}`,
      amount: orderValue,
      quantity: orderQuantity,
      pin: userPin,
    };

    setShowLoading(true);
    const json = JSON.stringify(searchParams);
    await axios
      .post("giftcards/buy.json", json)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setPurchase(result.data.data.gift_card);
          setShowPurchaseDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Please try again later.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={showConfirmDialog}>
        <DialogTitle>Authorize Transaction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDTypography variant="h6" fontWeight="light">
              Enter your pin to authorize transaction.
            </MDTypography>
            <MDBox mt={2}>
              <PinInput
                values={pinValues}
                onChange={(value, index, values) => setPinValues(values)}
                length={4}
                mask
                autoTab
                placeholder="-"
                onComplete={(text) => {
                  let up = "";
                  text.forEach((itm) => {
                    up += itm;
                  });
                  setUserPin(up);
                }}
              />
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="warning"
            variant="outlined"
            onClick={() => {
              setShowConfirmDialog(false);
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              if (userPin !== null) {
                setShowConfirmDialog(false);
                purchase();
              }
            }}
          >
            Proceed
          </MDButton>
        </DialogActions>
      </Dialog>
      {purchased !== null ? (
        <Dialog open={showPurchaseDialog}>
          <DialogTitle>Success!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <MDTypography variant="h6" fontWeight="light">
                Please find details of your gift card(s) purchase below:
              </MDTypography>
              <MDBox mt={3}>
                {purchased.redeem_codes.map((record) => (
                  <MDBox mt={3}>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                      <MDTypography variant="caption">Card Number:</MDTypography>
                      <MDTypography variant="caption" fontWeight="bold">
                        <b>{record.cardNumber}</b>
                        &nbsp;
                        <Icon
                          onClick={() => {
                            navigator.clipboard.writeText(record.cardNumber);
                            setSeverity("success");
                            setErrMsg(`Card number copied!`);
                            setOpen(true);
                          }}
                        >
                          copy
                        </Icon>
                      </MDTypography>
                    </MDBox>
                    {record.pinCode ? (
                      <MDBox
                        mt={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <MDTypography variant="caption">Pin code:</MDTypography>
                        <MDTypography variant="caption">
                          <b>{record.pinCode}</b>
                          &nbsp;
                          <Icon
                            onClick={() => {
                              navigator.clipboard.writeText(record.pinCode);
                              setSeverity("success");
                              setErrMsg(`Pin code copied!`);
                              setOpen(true);
                            }}
                          >
                            copy
                          </Icon>
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <> </>
                    )}
                    <MDBox mt={4}>
                      <MDTypography variant="h6">Redeem Instructions</MDTypography>
                      <MDTypography variant="caption">{giftCard.redeem_instruction}</MDTypography>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => {
                setShowPurchaseDialog(false);
                navigate("/giftcards");
              }}
              color="dark"
              variant="outlined"
            >
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      ) : (
        <> </>
      )}
      <MDBox pt={3} pb={3}>
        {giftCard !== null ? (
          <Card>
            <MDBox p={3}>
              <MDBox mb={3}>{giftCard.title}</MDBox>
              <Grid container spacing={4}>
                <Grid item xs={12} lg={6} md={6}>
                  <MDBox width="100%" display="flex" flexDirection="column" justifyContent="center">
                    <MDBox component="img" src={giftCard.logo} alt={giftCard.title} />
                    <MDBox mt={2}>
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDTypography variant="caption" fontWeight="medium">
                          Brand:
                        </MDTypography>
                        <MDTypography ml={2} variant="caption" fontWeight="medium">
                          {giftCard.brand}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox mt={2}>
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDTypography variant="caption" fontWeight="medium">
                          Country:
                        </MDTypography>
                        <MDTypography ml={2} variant="caption" fontWeight="medium">
                          {giftCard.country.title} &nbsp;
                          <img alt="" src={giftCard.country.flag} width="20" mr={3} />{" "}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} lg={6} md={6}>
                  <MDBox mb={3}>
                    <MDTypography variant="h6">Order Details</MDTypography>
                  </MDBox>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6} lg={6}>
                      <FormControl sx={{ minWidth: 120, width: "100%" }}>
                        <InputLabel>Units</InputLabel>
                        <Select
                          label="Units"
                          sx={{ padding: "12px", paddingLeft: "3px" }}
                          onChange={(e) => setOrderValue(e.target.value)}
                        >
                          {
                            // --> Notice below changes
                            giftCard.denominations.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                              giftCard.denominations.map((record) => (
                                <MenuItem value={record}>
                                  {giftCard.currency_code} {(record / 100).toFixed(2)}
                                </MenuItem>
                              ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            increment();
                          }}
                        >
                          <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                            add
                          </Icon>
                        </MDButton>
                        <MDInput
                          value={orderQuantity}
                          type="number"
                          onChange={(e) => {
                            setOrderQuantity(e.target.value);
                          }}
                          name="quantity"
                          label="Quantity"
                          sx={{ width: "60%" }}
                        />
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            decrement();
                          }}
                        >
                          <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                            remove
                          </Icon>
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                  <MDBox mt={4}>
                    <MDTypography variant="h6">Redeem Instructions</MDTypography>
                    <MDTypography variant="caption">{giftCard.redeem_instruction}</MDTypography>
                  </MDBox>
                  {calculated ? (
                    <MDBox mt={2}>
                      <Alert severity="info" sx={{ width: "100%" }}>
                        <MDBox>
                          <small variant="caption" fontWeight="heavy">
                            Ex. Rate: {(giftCard.exchange_rate / 100).toFixed(2)}
                          </small>
                        </MDBox>
                        <MDBox>
                          <small variant="caption" fontWeight="medium">
                            Fee: NGN {((orderQuantity * giftCard.fee) / 100).toFixed(2)}
                          </small>
                        </MDBox>
                        <MDBox>
                          <small variant="caption" fontWeight="medium">
                            You Pay: NGN {debitAmount.toFixed(2)}
                          </small>
                        </MDBox>
                      </Alert>
                    </MDBox>
                  ) : (
                    <> </>
                  )}

                  <MDBox mt={6} mb={2} sx={{ textAlign: "right" }}>
                    <MDButton
                      sx={{ minWidth: 120, width: "100%" }}
                      color="success"
                      variant="contained"
                      onClick={() => {
                        // purchase();
                        if (orderValue === null || orderQuantity < 1) {
                          return;
                        }
                        setUserPin(null);
                        setPinValues(["", "", "", ""]);
                        setShowConfirmDialog(true);
                      }}
                    >
                      Buy Gift Card
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        ) : (
          <> </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OrderGiftCard;
